import React, { useContext, useEffect, useState } from 'react';
import { Dialog, DialogContent, Grid, TextField } from '@mui/material';

import type { InputFields, ErrorMessages } from '../types';
import type { Office } from './types';
import MasterManageDialogHeaderButtons from '../master_manage_dialog_header_buttons';
import { apiContext } from '../../hooks/call_apis';

const OfficeManageDialog: React.FC<{
  setAlertMessage: CallableFunction;
  handleCloseDialog: () => void;
  open: boolean;
  updateTargetID: string | null;
  setUpdateTargetID: CallableFunction;
}> = ({ setAlertMessage, handleCloseDialog, open, updateTargetID, setUpdateTargetID }) => {
  const ctx = useContext(apiContext);
  const [officeInputs, setOfficeInputs] = useState<InputFields>({});
  const [changed, setChanged] = useState<boolean>(false);

  const [officeInputsError, setOfficeInputsError] = useState<ErrorMessages>({});

  const clearOffice = () => {
    setOfficeInputs({
      code: '',
      name: '',
      kana: '',
      name_short: '',
      postal_number: '',
      address1: '',
      address1_kana: '',
      address2: '',
      address2_kana: '',
      tel: '',
      fax: '',
    });
  };

  const clearOfficeError = () => {
    setOfficeInputsError({
      code: '',
      name: '',
      kana: '',
      name_short: '',
      postal_number: '',
      address1: '',
      address1_kana: '',
      address2: '',
      address2_kana: '',
      tel: '',
      fax: '',
    });
  };

  useEffect(() => {
    clearOffice();
    setChanged(false);
    if (updateTargetID) {
      ctx
        .getRecord<Office>('/api/offices/', updateTargetID)
        .then((data) => {
          setOfficeInputs({
            code: data.code,
            name: data.name,
            kana: data.kana,
            name_short: data.name_short,
            postal_number: data.postal_number,
            address1: data.address1,
            address1_kana: data.address1_kana,
            address2: data.address2,
            address2_kana: data.address2_kana,
            tel: data.tel,
            fax: data.fax,
          });
        })
        .catch((e) => {
          console.error(e);
        });
    }
    clearOfficeError();
  }, [ctx, updateTargetID, open]);

  return (
    <Dialog open={open} fullWidth maxWidth="xl">
      <DialogContent>
        <Grid container spacing={6}>
          <MasterManageDialogHeaderButtons
            url="/api/offices/"
            object={officeInputs}
            clearObjFunc={clearOffice}
            clearErrorFunc={clearOfficeError}
            handleCloseDialog={handleCloseDialog}
            setAlertMessage={setAlertMessage}
            setErrorFunc={setOfficeInputsError}
            updateTargetID={updateTargetID}
            setUpdateTargetID={setUpdateTargetID}
            backConfirm={changed}
          />
          <Grid item xs={12}>
            <Grid container spacing={5}>
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="code-inputs"
                  label="事業所コード"
                  type="text"
                  value={officeInputs.code ?? ''}
                  required
                  disabled={!!updateTargetID}
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, code: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  error={!!officeInputsError.code}
                  helperText={officeInputsError.code}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="name-inputs"
                  label="事業所名"
                  type="text"
                  value={officeInputs.name ?? ''}
                  required
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, name: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  error={!!officeInputsError.name}
                  helperText={officeInputsError.name}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="kana-inputs"
                  label="事業所名カナ"
                  type="text"
                  value={officeInputs.kana ?? ''}
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, kana: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  error={!!officeInputsError.kana}
                  helperText={officeInputsError.kana}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="name_short-inputs"
                  label="事業所名略称"
                  type="text"
                  value={officeInputs.name_short ?? ''}
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, name_short: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  required
                  error={!!officeInputsError.name_short}
                  helperText={officeInputsError.name_short}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="postal_number-inputs"
                  label="郵便番号"
                  type="text"
                  value={officeInputs.postal_number ?? ''}
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, postal_number: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  error={!!officeInputsError.postal_number}
                  helperText={officeInputsError.postal_number}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="address1-inputs"
                  label="住所1"
                  type="text"
                  value={officeInputs.address1 ?? ''}
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, address1: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  error={!!officeInputsError.address1}
                  helperText={officeInputsError.address1}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="address1_kana-inputs"
                  label="住所1カナ"
                  type="text"
                  value={officeInputs.address1_kana ?? ''}
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, address1_kana: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  error={!!officeInputsError.address1_kana}
                  helperText={officeInputsError.address1_kana}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="address2-inputs"
                  label="住所2"
                  type="text"
                  value={officeInputs.address2 ?? ''}
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, address2: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  error={!!officeInputsError.address2}
                  helperText={officeInputsError.address2}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="address2_kana-inputs"
                  label="住所2カナ"
                  type="text"
                  value={officeInputs.address2_kana ?? ''}
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, address2_kana: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  error={!!officeInputsError.address2_kana}
                  helperText={officeInputsError.address2_kana}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="tel-inputs"
                  label="電話番号"
                  type="text"
                  value={officeInputs.tel ?? ''}
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, tel: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  error={!!officeInputsError.tel}
                  helperText={officeInputsError.tel}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  id="fax-inputs"
                  label="Fax番号"
                  type="text"
                  value={officeInputs.fax ?? ''}
                  onChange={(event) => {
                    setOfficeInputs({ ...officeInputs, fax: event.target.value });
                    setChanged(true);
                  }}
                  size="small"
                  error={!!officeInputsError.fax}
                  helperText={officeInputsError.fax}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default OfficeManageDialog;
